import React from "react";
import { Box, Button, Stack, Text, Badge, Alert, Paper } from "@mantine/core";
import {
  IconDownload,
  IconFileText,
  IconAlertCircle,
} from "@tabler/icons-react";
import "./WordViewer.scss";

interface WordViewerProps {
  fileUrl: string;
  fileName?: string;
  className?: string;
}

const WordViewer: React.FC<WordViewerProps> = ({
  fileUrl,
  fileName,
  className = "",
}) => {
  // Determine if this is a .doc or .docx file based on filename
  const isLegacyDoc = fileName?.toLowerCase().endsWith(".doc") ?? false;
  const isModernDocx = fileName?.toLowerCase().endsWith(".docx") ?? false;

  const handleDownload = () => {
    try {
      // Create a temporary link to trigger download
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName || "document.doc";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading Word document:", err);
    }
  };

  return (
    <Paper className={`word-viewer ${className}`} p="xl">
      <Stack align="center" gap="lg" className="word-viewer-content">
        {/* Document Icon and Info */}
        <Box className="word-info" ta="center">
          <IconFileText size={64} className="word-icon" />
          <Text size="lg" fw={600} mt="md">
            {fileName || "Word Document"}
          </Text>

          <Badge
            color={isLegacyDoc ? "orange" : "blue"}
            variant="light"
            mt="sm"
          >
            {isLegacyDoc
              ? "Legacy Word Document (.doc)"
              : "Word Document (.docx)"}
          </Badge>
        </Box>

        {/* Information Alert */}
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Word Document Viewing"
          color="blue"
          variant="light"
          className="word-alert"
        >
          <Text size="sm">
            Word documents cannot be displayed directly in the browser. Download
            the file to view it in Microsoft Word or a compatible application.
          </Text>
        </Alert>

        {/* Download Button */}
        <Button
          leftSection={<IconDownload size={16} />}
          onClick={handleDownload}
          size="lg"
          className="download-button"
        >
          Download Document
        </Button>

        {/* Additional Info */}
        <Text size="xs" c="dimmed" ta="center" className="word-help">
          {isLegacyDoc && (
            <>
              Legacy .doc files require Microsoft Word or a compatible
              application to open.
            </>
          )}
          {isModernDocx && (
            <>
              Modern .docx files can be opened with Microsoft Word, Google Docs,
              or other compatible applications.
            </>
          )}
        </Text>
      </Stack>
    </Paper>
  );
};

export default WordViewer;
